import {
  AlertOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarcodeOutlined,
  BellOutlined,
  BranchesOutlined,
  BuildOutlined,
  CalculatorOutlined,
  CarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloudDownloadOutlined,
  ClusterOutlined,
  ContainerOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  DiffOutlined,
  DollarOutlined,
  ExperimentOutlined,
  FieldTimeOutlined,
  FileDoneOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FundProjectionScreenOutlined,
  IdcardOutlined,
  ImportOutlined,
  InboxOutlined,
  LoginOutlined,
  LogoutOutlined,
  NumberOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  PrinterOutlined,
  ProfileOutlined,
  PullRequestOutlined,
  QrcodeOutlined,
  ReconciliationOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  SolutionOutlined,
  SwapOutlined,
  TagsOutlined,
  TeamOutlined,
  ToolOutlined,
  TruckOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

/**
 * One icon per nav group, and one per screen under it.
 *
 * The sidebar was ninety lines of identical text, which reads as a list rather than as a system — the eye has nothing
 * to navigate by, so every trip through it is a read. Each icon is chosen for what the screen *does* in the store, not
 * for decoration: an inbox for goods arriving, a lorry for what has gone to a vendor, a rollback for a return, a
 * certificate for the green tag that says a part is serviceable.
 */
const GROUPS: Readonly<Record<string, ReactNode>> = {
  dash: <AppstoreOutlined />,
  inv: <SwapOutlined />,
  dmd: <FileTextOutlined />,
  proc: <ShoppingCartOutlined />,
  rcv: <InboxOutlined />,
  mro: <ToolOutlined />,
  loan: <DeploymentUnitOutlined />,
  mon: <BellOutlined />,
  wf: <CheckSquareOutlined />,
  mdm: <DatabaseOutlined />,
  admin: <SettingOutlined />,
};

const SCREENS: Readonly<Record<string, ReactNode>> = {
  // dashboards — each board is a different audience's view of the same section
  '/dash/stores': <ShopOutlined />,
  '/dash/proc': <ShoppingCartOutlined />,
  '/dash/mro': <ToolOutlined />,
  '/dash/loans': <DeploymentUnitOutlined />,
  '/dash/quality': <SafetyCertificateOutlined />,
  '/dash/mgmt': <FundProjectionScreenOutlined />,
  '/flow': <PartitionOutlined />,

  // stores — the shelf, and everything that moves on or off it
  '/inv/stock': <DatabaseOutlined />,
  '/inv/items': <ContainerOutlined />,
  '/inv/ledger': <OrderedListOutlined />,
  '/inv/counter': <BarcodeOutlined />,
  '/inv/issues': <LogoutOutlined />,
  '/inv/returns': <RollbackOutlined />,
  '/inv/tags': <TagsOutlined />,
  '/inv/transfers': <SwapOutlined />,
  '/inv/adjustments': <DiffOutlined />,
  '/inv/count': <CalculatorOutlined />,
  '/inv/counts': <CalculatorOutlined />,
  '/inv/labels': <PrinterOutlined />,
  '/inv/scan': <QrcodeOutlined />,

  // demand
  '/dmd/list': <FileTextOutlined />,
  '/dmd/generate': <ExperimentOutlined />,
  '/dmd/board': <ProjectBoard />,

  // procurement — the letters that go out and the orders that come back
  '/proc/rfqs': <FileSearchOutlined />,
  '/proc/quotes': <DollarOutlined />,
  '/proc/compare': <PullRequestOutlined />,
  '/proc/pos': <FileDoneOutlined />,
  '/proc/tracker': <FieldTimeOutlined />,

  // receiving
  '/rcv/list': <InboxOutlined />,
  '/rcv/inspect': <FileProtectOutlined />,
  '/rcv/putaway': <ImportOutlined />,
  '/rcv/disc': <WarningOutlined />,

  // repair & overhaul
  '/mro/backlog': <BuildOutlined />,
  '/mro/rfqs': <FileSearchOutlined />,
  '/mro/ros': <ToolOutlined />,
  '/mro/dns': <TruckOutlined />,
  '/mro/vendor': <CarOutlined />,
  '/mro/warranty': <SafetyCertificateOutlined />,
  '/mro/cal': <ExperimentOutlined />,

  // loans, both directions
  '/loan/list': <DeploymentUnitOutlined />,
  '/loan/request': <LoginOutlined />,
  '/loan/issue': <LogoutOutlined />,
  '/loan/outstanding': <ClockCircleOutlined />,

  // monitors — each one is a clock running against something
  '/mon/shelf': <ClockCircleOutlined />,
  '/mon/cal': <ExperimentOutlined />,
  '/mon/po': <ShoppingCartOutlined />,
  '/mon/ro': <ToolOutlined />,
  '/mon/disc': <WarningOutlined />,
  '/mon/wty': <SafetyCertificateOutlined />,
  '/mon/loan': <DeploymentUnitOutlined />,
  '/mon/alerts': <AlertOutlined />,

  // approvals
  '/wf/inbox': <CheckSquareOutlined />,
  '/wf/history': <AuditOutlined />,
  '/wf/delegations': <TeamOutlined />,
  '/wf/defs': <BranchesOutlined />,
  '/wf/matrix': <ApartmentOutlined />,

  // master data
  '/mdm/parts': <DatabaseOutlined />,
  '/mdm/partners': <BankOutlined />,
  '/mdm/aircraft': <ClusterOutlined />,
  '/mdm/locations': <ShopOutlined />,
  '/mdm/lookups': <UnorderedListOutlined />,
  '/mdm/wo': <ReconciliationOutlined />,

  // administration
  '/admin/users': <IdcardOutlined />,
  '/admin/roles': <SolutionOutlined />,
  '/admin/series': <NumberOutlined />,
  '/admin/company': <BankOutlined />,
  '/admin/settings': <SettingOutlined />,
  '/admin/themes': <AppstoreOutlined />,
  '/admin/labels': <PrinterOutlined />,
  '/admin/alerts': <AlertOutlined />,
  '/admin/audit': <AuditOutlined />,
  '/admin/import': <CloudDownloadOutlined />,
};

/** The demand board is a kanban; AntD has no board glyph, so the project one stands for it. */
function ProjectBoard() {
  return <ProfileOutlined />;
}

/** The icon for a nav group; an unmapped group falls back to the audit glyph rather than shifting the layout. */
export function NavIcon({ group }: { group: string }) {
  return (
    <span className="ico" aria-hidden="true">
      {GROUPS[group] ?? <AuditOutlined />}
    </span>
  );
}

/**
 * The icon for one screen. A route nobody mapped falls back to its group's glyph, so a new screen looks like the area
 * it belongs to from the day it appears rather than like a hole in the list.
 */
export function ScreenIcon({ route, group }: { route: string; group: string }) {
  return (
    <span className="ico" aria-hidden="true">
      {SCREENS[route] ?? GROUPS[group] ?? <AuditOutlined />}
    </span>
  );
}

/** Every route that carries an icon of its own — the test walks it against the nav. */
export const ICONED_ROUTES = Object.keys(SCREENS);
